import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { UserCheck, Plus, MoreHorizontal, Trash2, UserX, UserPlus, Eye, EyeOff, Copy, MapPin } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  "Conakry", "Boké", "Kindia", "Mamou", "Labé", "Faranah", "Kankan", "Nzérékoré"
];

export default function ControleursPage() {
  const { isSuperAdmin } = useAuth();
  const [controleurs, setControleurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, controleur: null });
  const [credentialsModal, setCredentialsModal] = useState({ open: false, credentials: null });
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    nom: "",
    prenom: "",
    telephone: "",
    region_affectation: ""
  });

  useEffect(() => {
    fetchControleurs();
  }, []);

  const fetchControleurs = async () => {
    try {
      const response = await axios.get(`${API_URL}/controleurs`);
      setControleurs(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des contrôleurs");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.nom || !formData.prenom) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/controleurs`, formData);
      toast.success("Contrôleur créé avec succès");
      setIsCreateOpen(false);
      setCredentialsModal({ open: true, credentials: response.data });
      setFormData({ email: "", nom: "", prenom: "", telephone: "", region_affectation: "" });
      fetchControleurs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (controleurId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/controleurs/${controleurId}/status?status=${newStatus}`);
      toast.success(`Contrôleur ${newStatus === "actif" ? "activé" : "suspendu"}`);
      fetchControleurs();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.controleur) return;
    
    try {
      await axios.delete(`${API_URL}/controleurs/${deleteDialog.controleur.id}`);
      toast.success("Contrôleur supprimé");
      setDeleteDialog({ open: false, controleur: null });
      fetchControleurs();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papiers");
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Accès réservé au Super Administrateur</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16" data-testid="controleurs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B4D3E]">Contrôleurs / Auditeurs</h1>
          <p className="text-muted-foreground">
            Gérez les comptes des contrôleurs IGT (accès lecture seule aux bilans)
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1B4D3E] hover:bg-[#163d32]" data-testid="create-controleur-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Contrôleur
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#1B4D3E]" />
                Créer un compte Contrôleur
              </DialogTitle>
              <DialogDescription>
                Le contrôleur aura accès en lecture seule aux bilans sociaux et pourra ajouter des observations.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    placeholder="Mamadou"
                    data-testid="controleur-prenom-input"
                  />
                </div>
                <div>
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    placeholder="DIALLO"
                    data-testid="controleur-nom-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="controleur@igt.gov.gn"
                  data-testid="controleur-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telephone">Téléphone</Label>
                <Input
                  id="telephone"
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  placeholder="+224 6XX XX XX XX"
                  data-testid="controleur-telephone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="region">Région d'affectation</Label>
                <Select
                  value={formData.region_affectation}
                  onValueChange={(value) => setFormData({...formData, region_affectation: value})}
                >
                  <SelectTrigger data-testid="controleur-region-select">
                    <SelectValue placeholder="Sélectionner une région (optionnel)" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map(region => (
                      <SelectItem key={region} value={region}>{region}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={creating}
                className="bg-[#1B4D3E] hover:bg-[#163d32]"
                data-testid="create-controleur-submit"
              >
                {creating ? "Création..." : "Créer le compte"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#1B4D3E]/10 rounded-lg">
                <UserCheck className="w-6 h-6 text-[#1B4D3E]" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Contrôleurs</p>
                <p className="text-2xl font-bold">{controleurs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserPlus className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Actifs</p>
                <p className="text-2xl font-bold text-green-600">
                  {controleurs.filter(c => c.status === "actif").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <UserX className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Suspendus</p>
                <p className="text-2xl font-bold text-orange-600">
                  {controleurs.filter(c => c.status === "suspendu").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Contrôleurs</CardTitle>
        </CardHeader>
        <CardContent>
          {controleurs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun contrôleur enregistré</p>
              <p className="text-sm">Cliquez sur "Nouveau Contrôleur" pour en créer un</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Région</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {controleurs.map((controleur) => (
                  <TableRow key={controleur.id} data-testid={`controleur-row-${controleur.id}`}>
                    <TableCell className="font-medium">
                      {controleur.prenom} {controleur.nom}
                    </TableCell>
                    <TableCell>{controleur.email}</TableCell>
                    <TableCell>{controleur.telephone || "-"}</TableCell>
                    <TableCell>
                      {controleur.region_affectation ? (
                        <Badge variant="outline" className="gap-1">
                          <MapPin className="w-3 h-3" />
                          {controleur.region_affectation}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={controleur.status === "actif" ? "default" : "secondary"}>
                        {controleur.status === "actif" ? "Actif" : "Suspendu"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(controleur.created_at).toLocaleDateString("fr-FR")}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {controleur.status === "actif" ? (
                            <DropdownMenuItem onClick={() => handleStatusChange(controleur.id, "suspendu")}>
                              <UserX className="w-4 h-4 mr-2 text-orange-600" />
                              Suspendre
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleStatusChange(controleur.id, "actif")}>
                              <UserCheck className="w-4 h-4 mr-2 text-green-600" />
                              Activer
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteDialog({ open: true, controleur })}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Credentials Modal */}
      <Dialog open={credentialsModal.open} onOpenChange={(open) => setCredentialsModal({ open, credentials: null })}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <UserCheck className="w-5 h-5" />
              Compte créé avec succès
            </DialogTitle>
          </DialogHeader>
          
          {credentialsModal.credentials && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <p className="font-medium text-green-800">
                  {credentialsModal.credentials.prenom} {credentialsModal.credentials.nom}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Email:</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-white px-2 py-1 rounded">
                        {credentialsModal.credentials.email}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(credentialsModal.credentials.email)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Mot de passe:</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-white px-2 py-1 rounded">
                        {showPassword ? credentialsModal.credentials.password : "••••••••"}
                      </code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(credentialsModal.credentials.password)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Un email avec ces identifiants a été envoyé au contrôleur.
                Le mot de passe devra être changé à la première connexion.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setCredentialsModal({ open: false, credentials: null })}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, controleur: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contrôleur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le compte de{" "}
              <strong>{deleteDialog.controleur?.prenom} {deleteDialog.controleur?.nom}</strong>{" "}
              sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
